import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import {
  bal,
  errorText,
  isConflict,
  Ledger,
  ledgerAllowed,
  ledgersApi,
  localDate,
  money,
  paise,
  Side,
  typeInfo,
  Voucher,
  VOUCHER_TYPES,
  VoucherIn,
  vouchersApi,
  VoucherType,
} from '../accounting.api';

interface LineDraft {
  key: number;
  ledgerId: number | null;
  side: Side;
  amount: string;
  narration: string;
}

interface Draft {
  type: VoucherType;
  date: string;
  referenceNo: string;
  referenceDate: string;
  narration: string;
  lines: LineDraft[];
}

let nextKey = 1;
const blankLine = (side: Side, amount = ''): LineDraft => ({ key: nextKey++, ledgerId: null, side, amount, narration: '' });

const freshDraft = (type: VoucherType, date = localDate()): Draft => ({
  type,
  date,
  referenceNo: '',
  referenceDate: '',
  narration: '',
  lines: [blankLine('Dr'), blankLine('Cr')],
});

const fromVoucher = (v: Voucher): Draft => ({
  type: v.type,
  date: v.date,
  referenceNo: v.referenceNo ?? '',
  referenceDate: v.referenceDate ?? '',
  narration: v.narration ?? '',
  lines: v.lines.map((l) => ({
    key: nextKey++,
    ledgerId: l.ledgerId,
    side: Number(l.debit) > 0 ? 'Dr' : 'Cr',
    amount: String(Number(l.debit) > 0 ? l.debit : l.credit),
    narration: l.narration ?? '',
  })),
});

/** Rupees and paise only: digits, one point, two decimals. */
const AMOUNT = /^\d{0,13}(\.\d{0,2})?$/;

/** What the side of a line means for each voucher type, shown as the field's hint. */
function sideHint(type: VoucherType, side: Side): string {
  if (type === 'PAYMENT') return side === 'Cr' ? 'Paid from (cash / bank)' : 'Paid to / expense';
  if (type === 'RECEIPT') return side === 'Dr' ? 'Received into (cash / bank)' : 'Received from / income';
  if (type === 'CONTRA') return side === 'Dr' ? 'Into (cash / bank)' : 'Out of (cash / bank)';
  return side === 'Dr' ? 'Debit (no cash / bank)' : 'Credit (no cash / bank)';
}

/**
 * Voucher entry and alteration, laid out like Tally: type tabs (F4–F7), date and reference,
 * then the lines — each a ledger with a debit or a credit — and running totals. Save stays
 * off until debits equal credits. The ledger list on each line only offers what the type
 * allows on that side (a payment is credited to cash / bank only, a journal never touches
 * them, …); the server checks the same rules again.
 *
 * Altering sends the version that was loaded; if someone else changed the voucher meanwhile
 * the server refuses (409) and the dialog offers to reload it.
 */
export default function VoucherEntryDialog({
  open,
  companyId,
  voucher,
  initialType = 'PAYMENT',
  onClose,
  onSaved,
}: {
  open: boolean;
  companyId: number | null;
  /** null enters a new voucher. */
  voucher: Voucher | null;
  initialType?: VoucherType;
  onClose: () => void;
  onSaved: (v: Voucher) => void;
}) {
  const [current, setCurrent] = useState<Voucher | null>(voucher);
  const [draft, setDraft] = useState<Draft>(() => (voucher ? fromVoucher(voucher) : freshDraft(initialType)));
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [loadingLedgers, setLoadingLedgers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const firstLedger = useRef<HTMLInputElement | null>(null);
  const altering = current !== null;

  // Reset whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setCurrent(voucher);
    setDraft(voucher ? fromVoucher(voucher) : freshDraft(initialType));
    setConflict(null);
  }, [open, voucher, initialType]);

  const loadLedgers = useCallback(() => {
    if (!companyId) return;
    setLoadingLedgers(true);
    ledgersApi
      .list(companyId)
      .then(setLedgers)
      .catch((e) => toast.error(errorText(e, 'Could not load ledgers', 'ACCOUNTING_LEDGERS_FAILED')))
      .finally(() => setLoadingLedgers(false));
  }, [companyId]);

  useEffect(() => {
    if (open) loadLedgers();
  }, [open, loadLedgers]);

  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);

  // ── Totals ──
  const drPaise = draft.lines.reduce((s, l) => s + (l.side === 'Dr' ? paise(l.amount) : 0), 0);
  const crPaise = draft.lines.reduce((s, l) => s + (l.side === 'Cr' ? paise(l.amount) : 0), 0);
  const diff = drPaise - crPaise;

  const problem = (() => {
    if (!draft.date) return 'Enter the date.';
    if (draft.lines.length < 2) return 'A voucher needs at least two lines.';
    if (draft.lines.some((l) => !l.ledgerId)) return 'Choose a ledger on every line.';
    if (draft.lines.some((l) => paise(l.amount) <= 0)) return 'Enter an amount on every line.';
    if (!draft.lines.some((l) => l.side === 'Dr') || !draft.lines.some((l) => l.side === 'Cr'))
      return 'A voucher needs at least one debit and one credit.';
    const ids = draft.lines.map((l) => l.ledgerId);
    if (new Set(ids).size !== ids.length) return 'A ledger appears more than once. Combine its lines.';
    const wrong = draft.lines.find((l) => {
      const led = l.ledgerId ? byId.get(l.ledgerId) : undefined;
      return led && !ledgerAllowed(draft.type, l.side, led);
    });
    if (wrong) return `${byId.get(wrong.ledgerId!)?.name} cannot be ${wrong.side === 'Dr' ? 'debited' : 'credited'} in a ${typeInfo(draft.type).label.toLowerCase()}.`;
    if (diff !== 0) return `Debits and credits differ by ${money(Math.abs(diff) / 100)}.`;
    return null;
  })();

  // ── Editing ──
  const patch = (key: number, p: Partial<LineDraft>) =>
    setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...p } : l)) }));

  /** A changed side may make the chosen ledger invalid; drop it then. */
  const setSide = (line: LineDraft, side: Side) => {
    const led = line.ledgerId ? byId.get(line.ledgerId) : undefined;
    patch(line.key, { side, ledgerId: led && !ledgerAllowed(draft.type, side, led) ? null : line.ledgerId });
  };

  const setType = (type: VoucherType) => {
    if (altering || type === draft.type) return;
    setDraft((d) => ({
      ...d,
      type,
      lines: d.lines.map((l) => {
        const led = l.ledgerId ? byId.get(l.ledgerId) : undefined;
        return led && !ledgerAllowed(type, l.side, led) ? { ...l, ledgerId: null } : l;
      }),
    }));
  };

  /** A new line takes the side and amount that would balance the voucher, as Tally does. */
  const addLine = () =>
    setDraft((d) => {
      const dr = d.lines.reduce((s, l) => s + (l.side === 'Dr' ? paise(l.amount) : 0), 0);
      const cr = d.lines.reduce((s, l) => s + (l.side === 'Cr' ? paise(l.amount) : 0), 0);
      const side: Side = dr > cr ? 'Cr' : 'Dr';
      return { ...d, lines: [...d.lines, blankLine(side, dr !== cr ? (Math.abs(dr - cr) / 100).toFixed(2) : '')] };
    });

  const removeLine = (key: number) => setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.key !== key) }));

  // ── Saving ──
  const payload = (): VoucherIn => ({
    type: draft.type,
    date: draft.date,
    referenceNo: draft.referenceNo.trim() || null,
    referenceDate: draft.referenceDate || null,
    narration: draft.narration.trim() || null,
    lines: draft.lines.map((l) => ({
      ledgerId: l.ledgerId!,
      debit: l.side === 'Dr' ? paise(l.amount) / 100 : null,
      credit: l.side === 'Cr' ? paise(l.amount) / 100 : null,
      narration: l.narration.trim() || null,
    })),
    version: current?.version ?? null,
  });

  const save = async (andNew = false) => {
    if (!companyId || problem || busy) return;
    setBusy(true);
    setConflict(null);
    try {
      const res = current ? await vouchersApi.alter(companyId, current.id, payload()) : await vouchersApi.create(companyId, payload());
      toast.success(res.message || `Voucher ${res.data.voucherNo} saved.`);
      onSaved(res.data);
      if (andNew) {
        setDraft(freshDraft(draft.type, draft.date));
        loadLedgers();
        setTimeout(() => firstLedger.current?.focus(), 50);
      } else {
        onClose();
      }
    } catch (e) {
      const msg = errorText(e, 'Could not save the voucher', 'ACCOUNTING_VOUCHER_SAVE_FAILED');
      if (isConflict(e)) setConflict(msg);
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reload = async () => {
    if (!companyId || !current) return;
    setBusy(true);
    try {
      const v = await vouchersApi.get(companyId, current.id);
      setCurrent(v);
      setDraft(fromVoucher(v));
      setConflict(null);
      loadLedgers();
      if (v.status === 'CANCELLED') toast.warning(`Voucher ${v.voucherNo} has been cancelled.`);
    } catch (e) {
      toast.error(errorText(e, 'Could not reload the voucher', 'ACCOUNTING_VOUCHER_LOAD_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  // Tally keys: F4–F7 pick the type of a new voucher; Ctrl+Enter / Ctrl+S save; Alt+N adds a line.
  const keys = useRef<(e: KeyboardEvent) => void>(() => {});
  keys.current = (e: KeyboardEvent) => {
    const t = VOUCHER_TYPES.find((x) => x.key === e.key);
    if (t && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setType(t.type);
    } else if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.key.toLowerCase() === 's')) {
      e.preventDefault();
      save();
    } else if (e.altKey && e.key.toLowerCase() === 'n') {
      e.preventDefault();
      addLine();
    }
  };
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => keys.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);

  const info = typeInfo(draft.type);
  const typeIndex = VOUCHER_TYPES.findIndex((t) => t.type === draft.type);
  const usedIds = new Set(draft.lines.map((l) => l.ledgerId).filter(Boolean));

  return (
    <ModalWrapper open={open} onClose={busy ? () => {} : onClose} maxWidth='lg' isFullWidth sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
      <SectionTitle
        title={altering ? `ALTER ${current!.voucherNo}` : 'VOUCHER ENTRY'}
        action={
          <IconButton onClick={onClose} disabled={busy} aria-label='Close'>
            <Icon icon='mdi:close' />
          </IconButton>
        }
      />
      {(loadingLedgers || busy) && <LinearProgress />}

      <Tabs value={typeIndex} onChange={(_, i) => setType(VOUCHER_TYPES[i].type)} variant='scrollable' scrollButtons='auto'>
        {VOUCHER_TYPES.map((t) => (
          <Tab
            key={t.type}
            disabled={altering && t.type !== draft.type}
            icon={<Icon icon={t.icon} width={18} />}
            iconPosition='start'
            label={
              <span>
                {t.label}{' '}
                <Typography component='span' variant='caption' color='text.secondary'>
                  {t.key}
                </Typography>
              </span>
            }
            sx={{ minHeight: 44 }}
          />
        ))}
      </Tabs>

      {conflict && (
        <Alert
          severity='warning'
          action={
            <Button color='inherit' size='small' onClick={reload} disabled={busy}>
              Reload
            </Button>
          }
        >
          {conflict} Reloading discards the changes made here.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '170px 1fr 170px' }, gap: 1.5 }}>
        <TextField
          label='Date'
          type='date'
          size='small'
          required
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label='Reference no.'
          size='small'
          value={draft.referenceNo}
          inputProps={{ maxLength: 60 }}
          onChange={(e) => setDraft({ ...draft, referenceNo: e.target.value })}
        />
        <TextField
          label='Reference date'
          type='date'
          size='small'
          value={draft.referenceDate}
          onChange={(e) => setDraft({ ...draft, referenceDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* ── Lines ── */}
      <Stack gap={1}>
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: '92px minmax(220px, 2fr) 150px minmax(140px, 1.4fr) 40px',
            gap: 1,
            px: 1,
          }}
        >
          {['Dr / Cr', 'Ledger', 'Amount', 'Line narration', ''].map((h) => (
            <Typography key={h} variant='caption' color='text.secondary' fontWeight={600}>
              {h}
            </Typography>
          ))}
        </Box>
        {draft.lines.map((line, i) => {
          const options = ledgers.filter(
            (l) =>
              (l.active || l.id === line.ledgerId) &&
              ledgerAllowed(draft.type, line.side, l) &&
              (!usedIds.has(l.id) || l.id === line.ledgerId)
          );
          const chosen = line.ledgerId ? (byId.get(line.ledgerId) ?? null) : null;
          return (
            <Box
              key={line.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '92px 1fr 40px', md: '92px minmax(220px, 2fr) 150px minmax(140px, 1.4fr) 40px' },
                gridTemplateAreas: {
                  xs: '"side amount remove" "ledger ledger ledger" "narr narr narr"',
                  md: '"side ledger amount narr remove"',
                },
                gap: 1,
                p: 1,
                alignItems: 'start',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: line.side === 'Dr' ? 'rgba(25, 118, 210, 0.03)' : 'rgba(46, 125, 50, 0.03)',
              }}
            >
              <ToggleButtonGroup
                exclusive
                size='small'
                value={line.side}
                onChange={(_, v) => v && setSide(line, v)}
                sx={{ gridArea: 'side', height: 40 }}
              >
                <ToggleButton value='Dr' sx={{ px: 1.25 }}>
                  Dr
                </ToggleButton>
                <ToggleButton value='Cr' sx={{ px: 1.25 }}>
                  Cr
                </ToggleButton>
              </ToggleButtonGroup>
              <Autocomplete
                sx={{ gridArea: 'ledger' }}
                size='small'
                options={options}
                value={chosen}
                loading={loadingLedgers}
                onChange={(_, v) => patch(line.key, { ledgerId: v?.id ?? null })}
                getOptionLabel={(l) => l.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  return q
                    ? opts.filter((o) => o.name.toLowerCase().includes(q) || (o.code ?? '').toLowerCase().includes(q) || o.groupName.toLowerCase().includes(q))
                    : opts;
                }}
                renderOption={(props, l) => {
                  const { key, ...rest } = props as typeof props & { key: string };
                  return (
                    <li key={key} {...rest}>
                      <Stack direction='row' justifyContent='space-between' gap={2} sx={{ width: '100%' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant='body2' noWrap>
                            {l.name}
                            {l.code ? ` (${l.code})` : ''}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {l.groupName}
                          </Typography>
                        </Box>
                        <Typography variant='caption' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {bal(l.currentBalance)}
                        </Typography>
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={i === 0 ? firstLedger : undefined}
                    placeholder={sideHint(draft.type, line.side)}
                    helperText={chosen ? `${chosen.groupName} · Cur. bal. ${bal(chosen.currentBalance)}` : sideHint(draft.type, line.side)}
                    error={!!chosen && !ledgerAllowed(draft.type, line.side, chosen)}
                  />
                )}
                noOptionsText={loadingLedgers ? 'Loading…' : 'No ledger fits this side of this voucher type'}
              />
              <TextField
                sx={{ gridArea: 'amount' }}
                size='small'
                placeholder='0.00'
                value={line.amount}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                onChange={(e) => {
                  const v = e.target.value.replace(/,/g, '');
                  if (AMOUNT.test(v)) patch(line.key, { amount: v });
                }}
                onBlur={() => line.amount && patch(line.key, { amount: (paise(line.amount) / 100).toFixed(2) })}
              />
              <TextField
                sx={{ gridArea: 'narr' }}
                size='small'
                placeholder='Line narration'
                value={line.narration}
                inputProps={{ maxLength: 255 }}
                onChange={(e) => patch(line.key, { narration: e.target.value })}
              />
              <Tooltip title='Remove line'>
                <span style={{ gridArea: 'remove' }}>
                  <IconButton size='small' onClick={() => removeLine(line.key)} disabled={draft.lines.length <= 2} sx={{ mt: 0.5 }}>
                    <Icon icon='mdi:delete-outline' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
        <Box>
          <Button size='small' startIcon={<Icon icon='mdi:plus' />} onClick={addLine} disabled={draft.lines.length >= 200}>
            Add line
          </Button>
        </Box>
      </Stack>

      {/* ── Totals ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 3 }}
        justifyContent='flex-end'
        alignItems={{ sm: 'center' }}
        sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant='body2'>
          Debit <b>{money(drPaise / 100)}</b>
        </Typography>
        <Typography variant='body2'>
          Credit <b>{money(crPaise / 100)}</b>
        </Typography>
        {diff === 0 && drPaise > 0 ? (
          <Chip size='small' color='success' icon={<Icon icon='mdi:check' />} label='Balanced' />
        ) : (
          <Chip
            size='small'
            color='error'
            label={`Difference ${money(Math.abs(diff) / 100)} ${diff > 0 ? 'Dr' : diff < 0 ? 'Cr' : ''}`.trim()}
          />
        )}
      </Stack>

      <TextField
        label='Narration'
        size='small'
        multiline
        minRows={2}
        value={draft.narration}
        inputProps={{ maxLength: 1000 }}
        onChange={(e) => setDraft({ ...draft, narration: e.target.value })}
      />

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent='space-between' alignItems={{ sm: 'center' }} gap={1}>
        <Typography variant='caption' color={problem ? 'error' : 'text.secondary'}>
          {problem ?? `${info.label} voucher ready. Ctrl+Enter saves.`}
        </Typography>
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {!altering && (
            <Button variant='outlined' onClick={() => save(true)} disabled={busy || !!problem || !companyId}>
              Save &amp; new
            </Button>
          )}
          <Button variant='contained' onClick={() => save()} disabled={busy || !!problem || !companyId}>
            {busy ? 'Saving…' : altering ? 'Save alteration' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </ModalWrapper>
  );
}
